import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  CompanyMetadataType,
  CompanyObject,
  CompanyTypes,
  UpdateCompanyDetailsMutation,
  UpdateCompanyDetailsMutationVariables,
  useGetAllTransactionsQuery,
  useGetCompanyWiseDataQuery,
  useUpdateCompanyDetailsMutation,
} from '../../../../generated/graphql'
import { InputBox } from '../../../../utils/NumberInputBox'
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG'
import { Modal } from 'react-bootstrap'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import Toast, { ToastType } from '../../../../utils/toast'
import { useEffect, useState } from 'react'

type Props = {
  companyMetadata?: CompanyMetadataType
  isEdit: boolean
  handleClose: () => void
  showModel: boolean
  existingData: CompanyMetadataType[]
  companyDetails: CompanyObject
  isIndirect: boolean
}

export function AddEditMetadataForm(props: Props) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<CompanyMetadataType[]>(props.existingData)
  const [keyList, setKeyList] = useState<string[]>([])

  useEffect(() => {
    setKeyList(props.existingData.map(() => crypto.randomUUID()))
  }, [props.existingData])

  const handleChange = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...data]
    updated[index][field] = value
    setData(updated)
  }

  const handleAdd = () => {
    setData((prev) => [...prev, { key: '', value: '' }])
    setKeyList((prev) => [...prev, crypto.randomUUID()])
  }

  const handleDelete = (deleteIndex: number) => {
    setData((prev) => prev.filter((_, i) => i !== deleteIndex))
    setKeyList((prev) => prev.filter((_, i) => i !== deleteIndex))
  }

  const mutateEditCompanyDetails = useUpdateCompanyDetailsMutation<Error>(
    graphqlRequestClient,
    {
      onSuccess: (
        data: UpdateCompanyDetailsMutation,
        _variables: UpdateCompanyDetailsMutationVariables,
        _context: unknown
      ) => {
        if (props.companyDetails) {
          const compKey = {
            input: {
              type: props.companyDetails?.type,
              hissaCompanyId: props.companyDetails?.hissaCompanyId || '',
              id: Number(props.companyDetails?.id) || 0,
            },
          }
          const getCompanyWiseQuery = useGetCompanyWiseDataQuery.getKey(compKey)
          queryClient.invalidateQueries(getCompanyWiseQuery)
        }
        const getAllTransactionQuery = useGetAllTransactionsQuery.getKey()
        queryClient.invalidateQueries(getAllTransactionQuery)
        props.handleClose()
        setLoading(false)
        Toast('Company Metadata Updated Successfully!', ToastType.success)
      },
      onError: (error: any) => {
        setLoading(false)
        Toast(error.response.errors[0].message, ToastType.error)
      },
    },
    {}
  )

  function UpdateCompanyValues() {
    const updatedData = data.filter((item) => item.key && item.value)
    const values = props.companyDetails
    props.companyDetails.type === CompanyTypes.Hissa
      ? mutateEditCompanyDetails.mutate({
          input: {
            cin: values.cin,
            companyName: values.name?.toUpperCase() || '',
            industry: values.industry,
            currentSharePrice: Number(values.sharePrice),
            currentValuation: Number(values.valuation),
            companyType: CompanyTypes.Hissa,
            companyId: Number(values.id),
            hissaCompanyId: values.hissaCompanyId,
            dba: values.dba,
            currency: values.companyCurrency,
            jurisdiction: values.jurisdiction,
            isIndirect: false,
            companyMetadata: updatedData,
          },
        })
      : mutateEditCompanyDetails.mutate({
          input: {
            cin: values.cin,
            companyName: values.name?.toUpperCase() || '',
            industry: values.industry,
            currentSharePrice: Number(values.sharePrice),
            currentValuation: Number(values.valuation),
            companyType: props.isIndirect ? CompanyTypes.Indirect : CompanyTypes.Direct,
            companyId: Number(values.id),
            dba: values.dba,
            currency: values.companyCurrency,
            jurisdiction: values.jurisdiction,
            isIndirect: props.isIndirect,
            companyMetadata: updatedData,
          },
        })
  }

  return (
    <Modal
      id='kt_modal_add_inv'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-650px'
      show={props.showModel}
      onHide={props.handleClose}
    >
      <Modal.Body className='pt-0 shadow border border-primary rounded'>
        <div className='col d-flex justify-content-between'>
          <div className='pt-9 pb-4'>
            <h2 className='card-header fw-bolder text-dark'>{'Add/Edit Company Details'}</h2>
          </div>
          <button
            className='btn btn-sm btn-icon btn-active-light-primary mt-6'
            type='button'
            data-bs-toggle='tooltip'
            title='Close'
            onClick={props.handleClose}
          >
            <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
          </button>
        </div>
        <div className='separator mb-6'></div>

        <div>
          {data.map((item, index) => (
            <div
              key={keyList[index]}
              className='d-flex justify-content-between align-items-center gap-6 mb-4'
            >
              <InputBox
                isDisabled={false}
                name='key'
                handleChange={() => {}}
                setFieldValue={(value: string) => {
                  handleChange(index, 'key', value)
                }}
                placeholder='Label'
                value={item.key || ''}
                type='text'
              />
              <InputBox
                isDisabled={false}
                name='value'
                handleChange={() => {}}
                setFieldValue={(value: string) => {
                  handleChange(index, 'value', value)
                }}
                placeholder='Value'
                value={item.value || ''}
                type='text'
              />
              <button
                onClick={() => handleDelete(index)}
                className='btn btn-sm btn-icon'
                type='button'
              >
                <KTSVG
                  path='/media/icons/duotune/general/gen027.svg'
                  className='svg-icon-1 text-danger cursor-pointer py-1 rounded bg-hover-secondary'
                />
              </button>
            </div>
          ))}

          <div className='col-12 d-flex justify-content-between mt-9'>
            <button
              type='button'
              onClick={() => handleAdd()}
              className='px-4 rounded border border-1 border-primary bg-transparent h-38px fw-normal text-primary bg-hover-secondary'
            >
              <KTSVG
                path='/media/icons/duotune/general/gen041.svg'
                className='svg-icon-3 text-primary'
              />{' '}
              Add New
            </button>

            <button
              type='submit'
              onClick={() => {
                setLoading(true)
                UpdateCompanyValues()
              }}
              disabled={loading}
              className='px-4 w-auto rounded border border-0 border-primary bg-primary h-38px fw-normal text-white'
            >
              <KTSVG
                path='/media/icons/duotune/files/fil009.svg'
                className='svg-icon-3 text-white'
              />{' '}
              {!loading && <span className='indicator-label'>Save</span>}
              {loading && (
                <span className='indicator-progress d-inline-flex align-items-center'>
                  Saving
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
